
import { defineComponent } from 'vue'
import { mapState } from 'vuex'
import { stripTrailingSlash } from '~/utils/string'

// Import State & Types
import { RunDetailActions } from '@/store/run/detail'

import ActiveUserMixin, { DashboardContext, SubscribedPlanInfo } from '~/mixins/activeUserMixin'
import { FeatureType } from '~/types/features'
import { TeamPerms } from '~/types/permTypes'
import { RunTypes } from '~/types/run'
import { Run, RunReportStatus } from '~/types/types'
import { resolveNodes } from '~/utils/array'
import { canAccessFeature } from '~/utils/billing'
import { extractHashId } from '~/utils/hashId'

export default defineComponent({
  name: 'RunDetails',
  middleware: [
    async function ({ store, route, error, redirect }) {
      const { provider, owner, repo, runId } = route.params
      const runResponse = (await store.dispatch(`run/detail/${RunDetailActions.FETCH_RUN}`, {
        provider,
        owner,
        name: repo,
        runId
      })) as Run | undefined

      // Show the UI state corresponding to archived runs after checking against the error message
      if (
        store.state.run.detail.error.message?.replace('GraphQL error: ', '') ===
        RunTypes.ARCHIVED_RUN
      ) {
        return
      }

      if (runResponse) {
        // If run has a completed report card and is not on the default branch, stay on this page to show it
        if (
          runResponse.reportCard?.status === RunReportStatus.Completed &&
          !runResponse.isForDefaultBranch
        ) {
          return
        }

        const { checks, scaChecks } = runResponse
        const code = resolveNodes(checks)?.[0]?.analyzer?.shortcode
        const scaCode = resolveNodes(scaChecks)?.[0]?.target?.id
        if (code) {
          redirect(302, `${stripTrailingSlash(route.path)}/${code}`)
          return
        } else if (scaCode) {
          redirect(302, `${stripTrailingSlash(route.path)}/sca/${extractHashId(scaCode)}`)
          return
        }
      }

      // Show `404` page if the run doesn't exist
      error({ statusCode: 404, message: 'This page is not real' })
    }
  ],
  layout: 'repository',
  mixins: [ActiveUserMixin],
  computed: {
    ...mapState('run/detail', {
      run: (state: any) => state.run as Run,
      storeError: (state: any) => state.error
    }),
    showReportCard(): boolean {
      return (
        this.run?.reportCard?.status === RunReportStatus.Completed && !this.run?.isForDefaultBranch
      )
    },
    showArchivedState(): boolean {
      return this.storeError?.message?.replace('GraphQL error: ', '') === RunTypes.ARCHIVED_RUN
    },
    dashboardContext(): DashboardContext {
      return this.activeDashboardContext as DashboardContext
    },
    isPrInsightsEligible(): boolean {
      return canAccessFeature(FeatureType.PR_INSIGHTS, this.dashboardContext?.subscribed_plan_info)
    },
    canChangePlan(): boolean {
      const role = this.dashboardContext?.role
      return role ? this.$gateKeeper.team(TeamPerms.CHANGE_PLAN, role) : false
    },
    subscribedPlanInfo(): SubscribedPlanInfo | undefined {
      return this.dashboardContext?.subscribed_plan_info
    }
  }
})
